import config from '../config';
import {
  customFetch,
  authHeader,
  jsonHeader,
  handleResponse,
  commonRequestOptions,
  subjects,
} from '../helpers';
import { datasetService } from './Dataset.service';

const endpointPath = config.api.API_URL + '/field';

export const fieldService = {
  getAll,
  getById,
  getByDatasetId,
  getByAttributeId,
  create,
  update,
  checkDestroy,
  destroy,
  updateFieldAssociation,
};

/**
 * Sends a GET request for fields, optionally narrowed by query parameters.
 * @param {string} [query] - Query string without the leading "?", e.g. `$filter=dataset_id eq 7`.
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ message: string, fields: object[] }>}
 */
function getAll(query, signal) {
  const requestOptions = { method: 'GET', headers: authHeader(), ...commonRequestOptions() };
  return customFetch(`${endpointPath}/all${query ? `?${query}` : ''}`, requestOptions, signal).then(
    handleResponse
  );
}

function getById(id, signal) {
  const requestOptions = { method: 'GET', headers: authHeader(), ...commonRequestOptions() };
  return customFetch(`${endpointPath}/${id}`, requestOptions, signal)
    .then(handleResponse)
    .then(res => {
      subjects.currentField.next(res.fields[0] || { field_id: id });
      subjects.currentDatasetGroup.next(
        res.fields[0]
          ? {
              dataset_group_id: res.fields[0].dataset_group_id,
              dataset_group_name: res.fields[0].dataset_group_name,
              datasource_hostname: res.fields[0].datasource_hostname,
              datasource_database: res.fields[0].datasource_database,
              datasource_type_name: res.fields[0].datasource_type_name,
              datasource_type_code: res.fields[0].datasource_type_code,
              datasource_type_category: res.fields[0].datasource_type_category,
            }
          : 'not_found'
      );
      subjects.currentDatasource.next(
        res.fields[0]
          ? {
              datasource_id: res.fields[0].datasource_id,
              datasource_name: res.fields[0].datasource_name,
              datasource_hostname: res.fields[0].datasource_hostname,
              datasource_database: res.fields[0].datasource_database,
              datasource_type_name: res.fields[0].datasource_type_name,
              datasource_type_code: res.fields[0].datasource_type_code,
              datasource_type_category: res.fields[0].datasource_type_category,
            }
          : 'not_found'
      );
      subjects.currentSystem.next(
        res.fields[0]
          ? {
              system_id: res.fields[0].system_id,
              system_name: res.fields[0].system_name,
            }
          : 'not_found'
      );

      // Resolve the dataset's ancestor chain, then publish the dataset crumb and its parent crumbs
      // together so they render in one pass (no staggered parent-crumb flash). The field row
      // carries dataset_id; ancestors are non-critical — an empty chain on failure or for a
      // top-level dataset.
      const datasetId = res.fields[0]?.dataset_id;
      const ancestorsRequest = datasetId
        ? datasetService.getAncestors(datasetId, signal).catch(() => ({ datasets: [] }))
        : Promise.resolve({ datasets: [] });

      ancestorsRequest.then(ancestorsRes => {
        subjects.currentDataset.next(
          res.fields[0]
            ? {
                dataset_id: res.fields[0].dataset_id,
                dataset_name: res.fields[0].dataset_name,
                dataset_type_name: res.fields[0].dataset_type_name,
                // API method fields let the dataset breadcrumb render the HTTP
                // method pill instead of the type icon for API endpoints.
                dataset_subtype_id: res.fields[0].dataset_subtype_id,
                dataset_subtype_name: res.fields[0].dataset_subtype_name,
                dataset_subtype_operation: res.fields[0].dataset_subtype_operation,
                dataset_group_id: res.fields[0].dataset_group_id,
                dataset_group_name: res.fields[0].dataset_group_name,
                datasource_hostname: res.fields[0].datasource_hostname,
                datasource_database: res.fields[0].datasource_database,
                datasource_type_name: res.fields[0].datasource_type_name,
                datasource_type_code: res.fields[0].datasource_type_code,
                datasource_type_category: res.fields[0].datasource_type_category,
              }
            : 'not_found'
        );
        subjects.currentDatasetAncestors.next({
          dataset_id: datasetId,
          ancestors: ancestorsRes.datasets,
        });
      });

      return res;
    });
}

function getByDatasetId(id, signal) {
  const requestOptions = { method: 'GET', headers: authHeader(), ...commonRequestOptions() };
  return customFetch(`${endpointPath}/dataset/${id}`, requestOptions, signal).then(handleResponse);
}

function getByAttributeId(id, signal) {
  const requestOptions = { method: 'GET', headers: authHeader(), ...commonRequestOptions() };
  return customFetch(`${endpointPath}/attribute/${id}`, requestOptions, signal).then(
    handleResponse
  );
}

function create(items, signal) {
  const requestOptions = {
    method: 'POST',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}`, requestOptions, signal).then(handleResponse);
}

function update(items, signal) {
  const requestOptions = {
    method: 'PUT',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}`, requestOptions, signal).then(handleResponse);
}

function checkDestroy(items, signal) {
  const requestOptions = {
    method: 'POST',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}/check_delete`, requestOptions, signal).then(handleResponse);
}

function destroy(items, signal) {
  const requestOptions = {
    method: 'DELETE',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}`, requestOptions, signal).then(handleResponse);
}

function updateFieldAssociation(items, signal) {
  const requestOptions = {
    method: 'PUT',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}/update_field_association`, requestOptions, signal).then(
    handleResponse
  );
}
